import * as React from "react"
import { actions, AppState } from "../data/redux-state"
import { globalStore } from "./Index"
import { IntlShape, useIntl } from "gatsby-plugin-intl"
import OutlinedInput from "@mui/material/OutlinedInput"
import InputLabel from "@mui/material/InputLabel"
import MenuItem from "@mui/material/MenuItem"
import FormControl from "@mui/material/FormControl"
import Select from "@mui/material/Select"
import {
  CoVarOption,
  createSelectableCoVarOptions,
  isMainObservationCoVarKey,
  postprocessCoVarsDescription,
  reorderCoVarOptions,
} from "../utils/extractCoVariables"

const ITEM_HEIGHT = 48
const ITEM_PADDING_TOP = 8
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 9 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
}

const sspPrefix = "SSP"
const sspMergedCoVar = "andSSPScens"
const defaultPredictCoVar = "andAnnPred"
const stDevAvsDCoVar = "StDevAvsD"

function sspIndividualCoVars(coVarOptions: CoVarOption[]): CoVarOption[] {
  return coVarOptions.filter((coVar) => {
    return coVar.value.startsWith(sspPrefix)
  })
}

function isURSDevEnabled(
  coVar: CoVarOption,
  type: "UR" | "sdev",
  appState: AppState
): boolean {
  const selectedCoVar = appState.coVarOptions.find(
    (selected) => selected.value === coVar.value
  )
  if (!selectedCoVar) {
    // if the coVar is not selected, then we consider UR and sdev as disabled
    return false
  }
  if (type === "UR") {
    return !!selectedCoVar.urEnabled
  } else if (type === "sdev") {
    return !!selectedCoVar.sdevEnabled
  }
  return false
}

function handleURSDevClick(
  clickEvent: any,
  clickedCoVar: CoVarOption,
  clickType: "UR" | "sdev",
  appState: AppState
) {
  clickEvent.stopPropagation() // to prevent duplicate event handling in the parent Select onChange
  const oldCoVars = appState.coVarOptions
  const includesClickedCoVar = oldCoVars.find(
    (oldCoVar) => oldCoVar.value === clickedCoVar.value
  )
  if (!includesClickedCoVar) {
    // special case: clicked on UR or sdev of a coVar that is not yet selected
    const freshlyAddedCoVar = {
      ...clickedCoVar,
      sdevEnabled: clickType === "sdev" ? true : false,
      urEnabled: clickType === "UR" ? true : false,
    }
    const coVars = [...oldCoVars, freshlyAddedCoVar]
    const coVarReordered = reorderCoVarOptions(coVars)
    globalStore.dispatch(actions.setCoVars(coVarReordered))
    return
  }
  const coVars: CoVarOption[] = []
  oldCoVars.forEach((oldCoVar) => {
    if (oldCoVar.value === clickedCoVar.value) {
      const newCoVar = { ...clickedCoVar, ...oldCoVar }
      if (clickType === "UR") {
        newCoVar.urEnabled = !oldCoVar.urEnabled
      } else if (clickType === "sdev") {
        newCoVar.sdevEnabled = !oldCoVar.sdevEnabled
      }
      // StDevAvsD: disabling its sdev removes the coVar entirely
      if (newCoVar.value === stDevAvsDCoVar && clickType === "sdev" && !newCoVar.sdevEnabled) {
        return
      }
      coVars.push(newCoVar)
    } else {
      coVars.push(oldCoVar)
    }
  })
  globalStore.dispatch(actions.setCoVars(coVars))
}

const SDevURSelector = (props: {
  appState: AppState
  coVar: CoVarOption
  type: "UR" | "sdev"
}) => {
  const intl = useIntl()
  const shouldShow = props.type === "UR" ? props.coVar.ur : props.coVar.sdev
  if (!shouldShow) {
    return <div style={{ display: "none" }}></div>
  }
  return (
    <div
      style={{
        marginRight: props.type === "UR" ? "5px" : undefined,
        border: "1px solid grey",
        paddingLeft: "5px",
        paddingRight: "5px",
        backgroundColor: isURSDevEnabled(
          props.coVar,
          props.type,
          props.appState
        )
          ? "#007bff"
          : "white",
      }}
      onClick={(e) =>
        handleURSDevClick(e, props.coVar, props.type, props.appState)
      }
    >
      {props.type === "UR"
        ? intl.formatMessage({
            id: props.coVar.urLabelId ?? "coVariables.90PCI",
          })
        : intl.formatMessage({
            id: props.coVar.sdevLabelId ?? "coVariables.StDev",
          })}
    </div>
  )
}

export const MultiCoVariablesDropdown = (props: {
  appState: AppState
  coVarOptions: CoVarOption[]
}) => {
  const intl = useIntl()
  const selectableCoVars: CoVarOption[] = createSelectableCoVarOptions(
    props.appState,
    props.coVarOptions,
    intl
  )
  React.useEffect(() => {
    const coVarsExceptMain = props.appState.coVarOptions.filter((coVar) => {
      return !isMainObservationCoVarKey(coVar.value)
    })
    const predictCoVarOption = selectableCoVars.find((coVar) => {
      return coVar.value === defaultPredictCoVar
    })
    if (!coVarsExceptMain.length) {
      if (!!predictCoVarOption) {
        console.log(
          `Found ${defaultPredictCoVar} and no other coVars -> enable ${defaultPredictCoVar} by default`
        )
        globalStore.dispatch(
          actions.setCoVars([
            ...props.appState.coVarOptions,
            predictCoVarOption,
          ])
        )
      }
    }
  }, [props.appState.sourceJson, props.appState.subVariable])

  // The Select value must keep the store's true selection order: MUI builds the
  // next selection from this array plus the clicked item, and the chart plots
  // later coVars in the foreground. Passing a dropdown-order-sorted array here
  // would re-canonicalize the z-order on every click. The user-visible label is
  // sorted separately in getMultiCoVarsSelectLabel.
  const selectedCoVars = props.appState.coVarOptions ?? []

  const handleCoVarOptionClick = async (event: any) => {
    const {
      target: { value },
    } = event
    const selectedCoVarKeys: string[] = value
    const coVars: Record<string, CoVarOption> = {}
    selectedCoVarKeys.forEach((coVarKey: string) => {
      // First check if this coVar is already selected to preserve urEnabled/sdevEnabled
      const existingCoVar = props.appState.coVarOptions.find(
        (coVar) => coVar.value === coVarKey
      )
      if (existingCoVar) {
        coVars[coVarKey] = existingCoVar
      } else {
        // Fall back to default options for newly selected coVars
        // Check selectableCoVars first (includes merged SSP option), then props.coVarOptions
        const coVarOption =
          selectableCoVars.find((coVar) => coVar.value === coVarKey) ||
          props.coVarOptions.find((coVar) => coVar.value === coVarKey)
        if (coVarOption) {
          coVars[coVarKey] = coVarOption
        } else {
          // should never happen because only selectableCoVars are shown
          console.error("Could not find coVarOption for key", coVarKey)
        }
      }
    })
    const sspCoVars = sspIndividualCoVars(props.coVarOptions)
    if (sspCoVars.length) {
      if (selectedCoVarKeys.includes(sspMergedCoVar)) {
        sspCoVars.forEach((sspCoVar: CoVarOption) => {
          coVars[sspCoVar.value] = sspCoVar
        })
      } else {
        sspCoVars.forEach((sspCoVar: CoVarOption) => {
          delete coVars[sspCoVar.value]
        })
      }
    }

    // StDevAvsD: selecting it auto-enables its sdev button
    const wasStDevAvsDSelected = props.appState.coVarOptions.some(
      (cv) => cv.value === stDevAvsDCoVar
    )
    if (coVars[stDevAvsDCoVar] && !wasStDevAvsDSelected) {
      coVars[stDevAvsDCoVar] = { ...coVars[stDevAvsDCoVar], sdevEnabled: true }
    }

    const rawCoVarOptions = Object.values(coVars)
    const coVarReordered = reorderCoVarOptions(rawCoVarOptions)
    globalStore.dispatch(actions.setCoVars(coVarReordered))
  }
  const emptySubSubVars = intl.formatMessage({
    id: "dropdown.emptySubSubVars",
  })
  // const coVarPrefix = intl.formatMessage({
  //   id: "dropdown.coVarPrefix",
  // })

  function getUserVisibleSelectLabel(coVar: { value: string; name: string }) {
    if (
      coVar.value === "andAnnPred" ||
      coVar.value === "andDMeanProj" ||
      coVar.value === "andParisScen" ||
      coVar.value === "sdev" ||
      coVar.value === "UR" ||
      coVar.value === sspMergedCoVar ||
      coVar.value === "andDMean"
    ) {
      return (
        // coVarPrefix +
        // " " +
        intl.formatMessage({ id: "coVariables." + coVar.value })
      )
    } else {
      return coVar.name
    }
  }

  function getMultiCoVarsSelectLabel(rawSelectedCoVarKeys: string[]) {
    if (!rawSelectedCoVarKeys.length) {
      return emptySubSubVars
    }
    // Keep the label text in stable dropdown order even though the underlying
    // selection array is in selection order (which drives the plot z-order).
    const dropdownOrder = new Map(
      selectableCoVars.map((coVar, index) => [coVar.value, index])
    )
    const selectedCoVarKeys = [...rawSelectedCoVarKeys].sort((left, right) => {
      return (
        (dropdownOrder.get(left) ?? Number.MAX_SAFE_INTEGER) -
        (dropdownOrder.get(right) ?? Number.MAX_SAFE_INTEGER)
      )
    })
    const value = selectedCoVarKeys[0]
    const name = intl.formatMessage({ id: "coVariables." + value })
    const coVar = { value, name }
    let multiSelectLabel = getUserVisibleSelectLabel(coVar)

    // special case for 2 or more selected coVars
    selectedCoVarKeys.slice(1).forEach((selection) => {
      if (selection.startsWith(sspPrefix)) {
        return // also hide sspPrefix-coVars in the select title
      }
      multiSelectLabel +=
        " " + intl.formatMessage({ id: "coVariables." + selection })
    })
    multiSelectLabel = postprocessCoVarsDescription(multiSelectLabel, intl)
    return multiSelectLabel
  }

  return (
    <>
      <FormControl
        style={{
          width: "70%",
          marginLeft: "10%",
          // marginTop: "10px",
        }}
      >
        <InputLabel
          style={{ backgroundColor: "white", left: "-13px" }}
          id="georegion-native-helper"
          shrink={false}
        >
          {selectedCoVars.length > 0 ? "" : emptySubSubVars}
        </InputLabel>
        <Select
          value={selectedCoVars.map((coVar) => coVar.value)}
          onChange={handleCoVarOptionClick}
          //   labelId="demo-multiple-name-label"
          // placeholder="Observed timeseries"
          id="georegion-native-helper"
          name="georegion"
          multiple
          input={<OutlinedInput label="CoVariable" />}
          MenuProps={MenuProps}
          style={{ textAlign: "left", paddingLeft: 0 }}
          renderValue={(selected: string[]) => {
            return getMultiCoVarsSelectLabel(selected)
          }}
        >
          {selectableCoVars.map((coVar, index) => {
            return (
              <MenuItem
                key={coVar.value}
                value={coVar.value}
                disabled={coVar.hasData === false}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    width: "100%",
                    justifyContent: "space-between",
                    opacity: coVar.hasData === false ? 0.8 : 1,
                  }}
                >
                  <div>{coVar.name}</div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                    }}
                  >
                    <SDevURSelector
                      appState={props.appState}
                      coVar={coVar}
                      type="UR"
                    />
                    <SDevURSelector
                      appState={props.appState}
                      coVar={coVar}
                      type="sdev"
                    />
                  </div>
                </div>
              </MenuItem>
            )
          })}
        </Select>
      </FormControl>
    </>
  )
}
